import { dangerouslyFetchAllPages } from '@eventbrite/http';
import * as Sentry from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { formatUrl } from 'url-lib';
import { createVenueAdapter } from '../adapters/venues';
import type { OrganizationVenuesResultDTO, VenueValues } from '../types';

/**
 * Filters out venues from blacklisted countries.
 *
 * @param venues - Array of adapted VenueValues
 * @param blacklistedCountries - Set of ISO 3166-1 Alpha-2 country codes to filter
 * @returns Filtered array with blacklisted country venues removed
 */
const filterBlacklistedVenues = (
    venues: VenueValues[],
    blacklistedCountries: Set<string>,
): VenueValues[] => {
    // Early return if no filtering needed
    if (blacklistedCountries.size === 0) {
        return venues;
    }

    return venues.filter((venue) => {
        const countryCode = venue.address.country;

        // Fail open: if country code is missing, keep the venue
        if (!countryCode) {
            return true;
        }

        // Filter out if country is in blacklist
        return !blacklistedCountries.has(countryCode);
    });
};

export const useOrganizationVenues = ({
    organizationId,
    blacklistedCountries = new Set<string>(),
}: {
    organizationId: string;
    blacklistedCountries?: Set<string>;
}) => {
    return useQuery({
        queryKey: [
            'organizationVenues',
            organizationId,
            Array.from(blacklistedCountries).sort().join(','),
        ],
        queryFn: () =>
            getOrganizationVenues(organizationId, blacklistedCountries),
        enabled: !!organizationId,
    });
};

export const getOrganizationVenues = (
    organizationId: string,
    blacklistedCountries: Set<string> = new Set<string>(),
): Promise<VenueValues[]> =>
    dangerouslyFetchAllPages(
        formatUrl(`/api/v3/organizations/${organizationId}/venues/`, {
            order_by: 'name_asc',
        }),
        'venues',
    )
        .then((result: OrganizationVenuesResultDTO) => {
            if (!result || !Array.isArray(result.objects)) {
                return [];
            }

            const adaptedVenues = result.objects
                .map((venue) => {
                    try {
                        return createVenueAdapter(venue);
                    } catch (error) {
                        // Log individual venue adapter errors but don't fail the entire request
                        Sentry.captureException(error, {
                            tags: {
                                endpoint:
                                    '/api/v3/organizations/{organizationId}/venues/',
                                component: 'useOrganizationVenues',
                                error_type: 'venue_adapter_error',
                            },
                            extra: {
                                organizationId,
                                venue: JSON.stringify(venue),
                                message: 'Failed to adapt venue data',
                            },
                        });
                        return null;
                    }
                })
                .filter((venue): venue is VenueValues => venue !== null);

            // Apply country-based filtering
            return filterBlacklistedVenues(adaptedVenues, blacklistedCountries);
        })
        .catch((error: Error) => {
            Sentry.captureException(error, {
                tags: {
                    endpoint: '/api/v3/organizations/{organizationId}/venues/',
                    service: 'venueService',
                },
                extra: {
                    organizationId,
                    message: 'Failed to fetch organization venues',
                },
            });

            throw error;
        });
